import { CalendarPlus, PawPrint } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PetCard, type PetSummary } from '../components/PetCard';
import { customerApi } from '../customer_api';

export function Pets() {
  const [pets, setPets] = useState<PetSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    customerApi<{ pets: PetSummary[] }>('/api/customer/pets')
      .then((data) => setPets(data.pets))
      .catch(() => setError('Could not load pets. Please try again.'));
  }, []);

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <span
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Your pets
        </span>
        <h1
          style={{
            fontSize: '2.25rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0.4rem 0 1.5rem',
          }}
        >
          Pets
        </h1>

        {error && <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{error}</p>}

        {!error && pets === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {pets !== null && pets.length === 0 && (
          <div
            style={{
              marginTop: '2rem',
              padding: '3rem 2rem',
              borderRadius: '14px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              textAlign: 'center',
              background: 'var(--surface, white)',
            }}
          >
            <div
              style={{
                width: '4rem',
                height: '4rem',
                borderRadius: '14px',
                background: 'oklch(0.94 0.02 263)',
                color: 'var(--primary, oklch(0.43 0.18 263))',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.25rem',
              }}
            >
              <PawPrint size={28} strokeWidth={1.5} />
            </div>
            <h2
              style={{
                margin: '0 0 0.5rem',
                fontSize: '1.25rem',
                fontWeight: 500,
              }}
            >
              No pets on file yet
            </h2>
            <p
              style={{
                margin: '0 0 1.5rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                fontSize: '0.95rem',
              }}
            >
              The kennel will add your pets&apos; details when you book your first stay. Start by
              booking — we&apos;ll capture each pet&apos;s details during the booking flow.
            </p>
            <Link
              to="/book"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.85rem 1.5rem',
                borderRadius: '10px',
                background: 'var(--primary, oklch(0.43 0.18 263))',
                color: 'white',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
              }}
            >
              <CalendarPlus size={18} />
              Book your first stay
            </Link>
          </div>
        )}

        {pets !== null && pets.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '1.25rem',
            }}
          >
            {pets.map((p) => (
              <PetCard key={p.id} pet={p} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
